import React, { useEffect, useMemo, useRef, useState } from "react";
import Setup from "../pages/setup/Setup";
import ApproveDeclineSetup from "../pages/setup/ApproveDeclineSetup";
import Main from "../components/layout/Main";
import ProtectedRoute from "../components/comps/protected_route";
import Settings from "../dependencies/custom/settings";
import Config, {
  fuelApproDeclCols,
  allFuelRequestsCols,
  maintApproDeclCols,
  allMaintRequestsCols,
} from "../pages/setup/Config";
import useTable from "../hooks/table";
import useDelete from "../hooks/rest_delete";
import { useNavigate, useLocation } from "react-router-dom";
import ValuesStore from "../store/values-store";
import MyJSX from "../pages/setup/MyJSX";
import useExportPDF from "../hooks/pdf";
import PrimaryAssessment from "../pages/academics_mk/assessment/PrimaryAssessment";
import PrimaryAssessmentReview from "../pages/academics_mk/assessment/PrimaryAssessmentReview";
import NurseryAssessmentReview from "../pages/academics_mk/assessment/NurseryAssessmentReview";
import NurseryAssessment from "../pages/academics_mk/assessment/NurseryAssessment";
import Attendance from "../pages/academics_mk/attendance/Attendance";
import GradingSystemManagement from "../pages/academics_mk/grading/GradingSystemManagement";
import PreschoolGradingManagement from "../pages/academics_mk/grading/PreschoolGradingManagement";
import InstructionalMaterials from "../pages/academics_mk/instructional/InstructionalMaterials";
import MaterialsApproval from "../pages/academics_mk/instructional/MaterialsApproval";

const useAcademicRoutes = () => {
  const valuesStore = ValuesStore();
  const pdfExport = useExportPDF();
  const [editRecord, setEditRecord] = useState();
  const [deleteResponse, setDeleteResponse] = useState();
  const [apprvDeclRecord, setApprDeclRecord] = useState();
  const rejectionRemark = useRef();
  const [itemToView, setItemToview] = useState();
  const [itemToViewSetup, setItemToViewSetup] = useState();
  const [rerender, setRerender] = useState(false);
  const myJSX = MyJSX();

  const table = useTable();
  const del = useDelete();
  const navigate = useNavigate();

  const perms = valuesStore.getValue("permissions");
  const conf = Config(
    table,
    del,
    setEditRecord,
    onSetupDelete,
    pdfExport,
    navigate,
    setRerender,
    setItemToViewSetup,
    valuesStore,
    perms,
  );

  function onSetupDelete(a, b, c) {
    setDeleteResponse(b);
  }

  const location = useLocation();
  useEffect(() => {
    setEditRecord(undefined);
    setItemToview(undefined);
    setItemToViewSetup(undefined);
  }, [location.pathname]);

  return [
    {
      path: "/admin",
      element: <Main />,
      children: [
        {
          path: "assign_subjects",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                recordToEdit={editRecord}
                deleteResponse={deleteResponse}
                itemToView={itemToViewSetup}
                setItemToview={setItemToViewSetup}
                viewJSX={myJSX.fuelJSX}
                split={["subjectID", "classID", "streamID"]}
                getEndpoint={Settings.urls.academics.subject_assignments}
                saveEndpoint={Settings.urls.academics.subject_assignments_save}
                httpMethod={"POST"}
                keyName={"id"}
                label={conf?.SUBJECT_ASSIGNMENTS?.label}
                tableName={conf?.SUBJECT_ASSIGNMENTS?.tableName}
                columns={conf?.SUBJECT_ASSIGNMENTS?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "primary_assessment_review",
          element: (
            <ProtectedRoute>
              <PrimaryAssessmentReview key="review" title="Assessment Review" />
            </ProtectedRoute>
          ),
        },
        {
          path: "primary_assessment_publish",
          element: (
            <ProtectedRoute>
              <PrimaryAssessmentReview
                key="publish"
                title="Assessment Publication"
                isHeadTeacher={true}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "nursery_assessment_review",
          element: (
            <ProtectedRoute>
              <NurseryAssessmentReview key="review" title="Assessment Review" />
            </ProtectedRoute>
          ),
        },
        {
          path: "nursery_assessment_publish",
          element: (
            <ProtectedRoute>
              <NurseryAssessmentReview
                key="publish"
                title="Assessment Publication"
                isHeadTeacher={true}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "attendance",
          element: (
            <ProtectedRoute>
              <Attendance />
            </ProtectedRoute>
          ),
        },
        {
          path: "my_subjects",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                getEndpoint={Settings.urls.academics.my_subjects}
                keyName={"id"}
                label={conf?.MY_ASSIGNMENTS?.label}
                tableName={conf?.MY_ASSIGNMENTS?.tableName}
                columns={conf?.MY_ASSIGNMENTS?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "class_management",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                getEndpoint={Settings.urls.academics.class_subjects_v2}
                keyName={"id"}
                label={conf?.CLASS_SUBJECTS?.label}
                tableName={conf?.CLASS_SUBJECTS?.tableName}
                columns={conf?.CLASS_SUBJECTS?.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "students",
          element: (
            <ProtectedRoute>
              <Setup
                setRecordToEdit={setEditRecord}
                resultKey={"students"}
                getEndpoint={Settings?.urls?.academics?.students}
                keyName={"id"}
                label={conf.STUDENTS.label}
                tableName={conf.STUDENTS.tableName}
                columns={conf.STUDENTS.columns}
              />
            </ProtectedRoute>
          ),
        },
        {
          path: "primary_assessment_entry",
          element: (
            <ProtectedRoute>
              <PrimaryAssessment />
            </ProtectedRoute>
          ),
        },
        {
          path: "nursery_assessment_entry",
          element: (
            <ProtectedRoute>
              <NurseryAssessment />
            </ProtectedRoute>
          ),
        },
        {
          path: "grading_systems",
          element: (
            <ProtectedRoute>
              <GradingSystemManagement />
            </ProtectedRoute>
          ),
        },
        {
          path: "preschool_grading",
          element: (
            <ProtectedRoute>
              <PreschoolGradingManagement />
            </ProtectedRoute>
          ),
        },
        {
          path: "instructional_materials",
          element: (
            <ProtectedRoute>
              <InstructionalMaterials />
            </ProtectedRoute>
          ),
        },
        {
          path: "materials_approval_hod",
          element: (
            <ProtectedRoute>
              <MaterialsApproval role="hod" />
            </ProtectedRoute>
          ),
        },
        {
          path: "materials_approval_section_head",
          element: (
            <ProtectedRoute>
              <MaterialsApproval role="section_head" />
            </ProtectedRoute>
          ),
        },
        {
          path: "materials_approval_deputy_head",
          element: (
            <ProtectedRoute>
              <MaterialsApproval role="deputy_head" />
            </ProtectedRoute>
          ),
        },
        {
          path: "materials_approval_headteacher",
          element: (
            <ProtectedRoute>
              <MaterialsApproval role="headteacher" />
            </ProtectedRoute>
          ),
        },
      ],
    },
  ];
};

export default useAcademicRoutes;
